import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FeaturedTournaments } from '../components/FeaturedTournaments';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { CardGridSkeleton } from '../components/PageSkeletons';
import { PullToRefresh } from '../components/PullToRefresh';
import axios from 'axios';
import { 
  Trophy, 
  Plus, 
  Search, 
  Calendar, 
  Users, 
  DollarSign,
  Filter,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];
const STATUSES = ['All', 'upcoming', 'active', 'completed'];

export const Tournaments = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ageFilter, setAgeFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to fetch tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTournaments = tournaments.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase());
    const matchesAge = ageFilter === 'All' || t.age_category === ageFilter;
    const matchesGender = genderFilter === 'All' || t.gender === genderFilter;
    const matchesStatus = statusFilter === 'All' || t.status === statusFilter;
    return matchesSearch && matchesAge && matchesGender && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
      default: return '';
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <CardGridSkeleton count={6} />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchTournaments}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="tournaments-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">{t('tournament.title').toUpperCase()}</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('tournament.browseDescription') || 'Browse and join flag football tournaments'}
          </p>
        </div>
        
        {(user?.role === 'admin' || user?.role === 'organizer') && (
          <Link to="/tournaments/create" className="mt-4 md:mt-0">
            <Button className="btn-primary gap-2" data-testid="create-tournament-btn">
              <Plus className="w-4 h-4" />
              {t('tournament.createTournament')}
            </Button>
          </Link>
        )}
      </div>

      {/* Featured Tournaments */}
      <FeaturedTournaments period="week" limit={3} />

      {/* Filters */}
      <Card className="mb-8" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search tournaments..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="age-filter">
                <SelectValue placeholder="Age" />
              </SelectTrigger>
              <SelectContent>
                {AGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="gender-filter">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map(s => (
                  <SelectItem key={s} value={s}>{s === 'All' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tournaments Grid */}
      {filteredTournaments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTournaments.map((tournament, index) => (
            <Card 
              key={tournament.id} 
              className="card-hover overflow-hidden"
              data-testid={`tournament-card-${index}`}
            >
              <div className="h-2 bg-gradient-to-r from-primary to-accent" />
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <Badge variant="outline" className={getStatusColor(tournament.status)}>
                    {tournament.status.toUpperCase()}
                  </Badge>
                  <Badge variant="secondary">
                    {tournament.age_category} • {tournament.gender}
                  </Badge>
                </div>
                <CardTitle className="text-xl font-bold mt-2">{tournament.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {tournament.description}
                </p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>{formatDate(tournament.start_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span>{tournament.registered_teams}/{tournament.max_teams} Teams</span>
                  </div>
                  <div className="flex items-center gap-2 col-span-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <span className="text-xs">
                      ${tournament.entry_fee} entry + $10 registration = ${(tournament.entry_fee + 10).toFixed(2)} total
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Trophy className="w-4 h-4 text-muted-foreground" />
                    <span>{tournament.fields?.length || 0} Fields</span>
                  </div>
                </div>
                
                <Link to={`/tournaments/${tournament.id}`}>
                  <Button className="w-full gap-2" variant="outline" data-testid={`view-tournament-${index}`}>
                    View Details
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12" data-testid="no-tournaments">
          <CardContent>
            <Trophy className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Tournaments Found</h3>
            <p className="text-muted-foreground mb-6">
              {search || ageFilter !== 'All' || genderFilter !== 'All' || statusFilter !== 'All'
                ? 'Try adjusting your filters'
                : 'Be the first to create a tournament'}
            </p>
            {(user?.role === 'admin' || user?.role === 'organizer') && (
              <Link to="/tournaments/create">
                <Button className="btn-primary gap-2">
                  <Plus className="w-4 h-4" />
                  Create Tournament
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}
    </div>
    </PullToRefresh>
  );
};
